import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Menu, X, ArrowUpRight } from "lucide-react";
import { LOGO, NAV } from "@/data/site";

export const Header = ({ onJoin }) => {
  const [solid, setSolid] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setSolid(window.scrollY > 40);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      data-testid="site-header"
      className={`fixed top-0 left-0 right-0 z-50 ${solid ? "glass border-b border-[rgba(111,227,155,0.16)]" : ""}`}
      style={{ transition: "background-color .5s ease, border-color .5s ease" }}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-[76px] flex items-center justify-between">
        <a href="#top" data-testid="logo-link" className="flex items-center gap-3">
          <img
            src={LOGO}
            alt="KMG Digi Platform"
            className="h-10 w-auto rounded-sm object-contain"
          />
        </a>

        <nav className="hidden lg:flex items-center gap-8">
          {NAV.map((item) => (
            <a
              key={item.href}
              href={item.href}
              data-testid={`nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
              className="font-mono-label text-[11px] uppercase tracking-[0.18em] text-[color:var(--kmg-bone-dim)] underline-grow hover:text-[color:var(--kmg-lime)]"
              style={{ transition: "color .35s ease" }}
            >
              {item.label}
            </a>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <button
            data-testid="header-join-btn"
            onClick={onJoin}
            className="hidden sm:inline-flex items-center gap-2 rounded-full border border-[color:var(--kmg-lime)] px-5 py-2.5 text-xs font-mono-label uppercase tracking-[0.16em] text-[color:var(--kmg-lime)] hover:bg-[color:var(--kmg-lime)] hover:text-[color:var(--kmg-forest)]"
            style={{ transition: "background-color .4s ease, color .4s ease" }}
          >
            Join Platform <ArrowUpRight size={14} />
          </button>
          <button
            data-testid="mobile-menu-toggle"
            onClick={() => setOpen((v) => !v)}
            className="lg:hidden p-2 text-[color:var(--kmg-bone)]"
            aria-label="Menu"
          >
            {open ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            data-testid="mobile-menu"
            initial={{ height: 0, opacity: 0 }}
            animate={{ height: "auto", opacity: 1 }}
            exit={{ height: 0, opacity: 0 }}
            transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
            className="lg:hidden glass border-t border-[rgba(111,227,155,0.16)] overflow-hidden"
          >
            <div className="px-6 py-8 flex flex-col gap-5">
              {NAV.map((item) => (
                <a
                  key={item.href}
                  href={item.href}
                  onClick={() => setOpen(false)}
                  data-testid={`mobile-nav-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
                  className="font-display text-xl"
                >
                  {item.label}
                </a>
              ))}
              <button
                data-testid="mobile-join-btn"
                onClick={() => {
                  setOpen(false);
                  onJoin();
                }}
                className="mt-2 rounded-full bg-[color:var(--kmg-lime)] px-6 py-3 text-sm font-semibold text-[color:var(--kmg-forest)]"
              >
                Join Platform
              </button>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </header>
  );
};
